import { FluidIconConfig, FluidIconKey, FluidTheme } from '@lmig/fluid-core';
import { FunctionalComponent, h } from '@stencil/core';

import { FluidFunctionalComponentProps } from '../../../model/functional/fluid-functional-component-props.interface';
import { themeVar } from '../../../utils/themes/theme.variables';

export interface FluidCarouselProps extends FluidFunctionalComponentProps {
  slides?: HTMLElement[];
  activeSlide?: HTMLElement;
  setActiveSlide?: (slide: HTMLElement) => void;
  previousSlide?: () => void;
  nextSlide?: () => void;
  theme: FluidTheme;
}

export interface CarouselNavigationProps extends FluidFunctionalComponentProps {
  navigate: () => void;
  icon: FluidIconKey;
  theme: FluidTheme;
}

// ================================================================ //
// -- Public Functional Components

/**
 * SlideIndicators - iOS-style buttons representing each slide that
 * trigger navigation on click
 */
export const SlideIndicators: FunctionalComponent<FluidCarouselProps> = (
  props: FluidCarouselProps
) => {
  const { slides, activeSlide, setActiveSlide } = props;

  return (
    <div class='slide-indicators-container'>
      {slides?.map((slide) => (
        <button
          onClick={() => setActiveSlide(slide)}
          class={{
            'slide-indicator': true,
            'slide-active': activeSlide.dataset.id === slide.dataset.id,
          }}
        />
      ))}
    </div>
  );
};

/**
 * SlideNavButton - next / previous Button
 */
export const SlideNavButton: FunctionalComponent<CarouselNavigationProps> = (
  props: CarouselNavigationProps
) => {
  const { navigate, icon, theme } = props;
  const iconConfig = {
    ...themeVar<FluidIconConfig>(theme, 'icons.carousel.slide_controls'),
    key: icon,
  };

  return (
    <fluid-button
      icon={iconConfig}
      removeMargin={true}
      theme={theme}
      overrideGlobalTheme={true}
      onClick={() => navigate()}
      class={!!props.class && `${props.class}`}
    />
  );
};

/**
 * CarouselNavigation - next / previous Buttons & slide indicators
 */
export const CarouselNavigation: FunctionalComponent<FluidCarouselProps> = (
  props: FluidCarouselProps
) => {
  const { previousSlide, nextSlide, theme } = props;

  return (
    <div class='nav-controls-container'>
      <SlideNavButton
        class='previous-button'
        navigate={() => previousSlide()}
        icon={FluidIconKey.ARROW_LEFT}
        theme={theme}
      />
      <SlideIndicators {...props} />
      <SlideNavButton
        class='next-button'
        navigate={() => nextSlide()}
        icon={FluidIconKey.ARROW_RIGHT}
        theme={theme}
      />
    </div>
  );
};
